/**
 * BaseDialogNode - Shared base component for all dialog field nodes
 * 
 * Provides common functionality:
 * - CardBase wrapper with selection border
 * - Target handle (left side)
 * - Section name display with inline editing
 * - Header with icon, type label, and edit toggle
 * - Children slot for type-specific content
 */

import React, { useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import IconProvider from '@/components/ui/IconProvider';
import CardBase from '@/components/cards/CardBase';
import { Input } from '@/components/ui/InputDefault';
import { DialogField, DialogNode } from '@/lib/stores/dialogStore';
import { useDialogNodeContextOptional } from '@/contexts/DialogNodeContext';
import { 
  DialogFieldNodeData, 
  getFieldTypeIcon, 
  getFieldTypeColor, 
  getFieldTypeLabel,
  getNodeName as getNodeNameUtil,
  getSectionName as getSectionNameUtil 
} from './dialogNodeUtils';

export interface BaseDialogNodeProps {
  data: DialogFieldNodeData;
  selected: boolean;
  children: ReactNode;
  // Flow node ID (for matching with selected node context)
  id?: string;
  // Optional override for header content (for edit mode type selector)
  headerContent?: ReactNode;
  // Whether to show the edit button in header
  showEditButton?: boolean;
  // Whether currently in edit mode
  isEditing?: boolean;
  // Callback when edit button is clicked
  onEditToggle?: () => void;
  // Optional additional class names for the card
  className?: string;
}

export function BaseDialogNode({
  data,
  selected,
  children,
  id,
  headerContent,
  showEditButton = true,
  isEditing = false,
  onEditToggle,
  className = '',
}: BaseDialogNodeProps) {
  // Support both new (node) and legacy (field) structure
  const node = data.node || data.field;
  const nodeName = data.nodeName || data.sectionName || '';
  const nodeIndex = data.nodeIndex ?? data.sectionIndex ?? 0;
  
  // Check if this node is selected in the context (for node-info sidebar)
  const dialogNodeContext = useDialogNodeContextOptional();
  const isSelectedInContext = dialogNodeContext?.selectedNodeId === id;
  
  const [isEditingNodeName, setIsEditingNodeName] = useState(false);
  const [editedNodeName, setEditedNodeName] = useState(nodeName);

  // Sync node name with prop changes when not editing
  useEffect(() => {
    if (!isEditingNodeName && !isEditing) {
      setEditedNodeName(nodeName);
    }
  }, [nodeName, isEditingNodeName, isEditing]);

  // Save node name when exiting edit mode (if changed)
  const prevIsEditingRef = useRef(isEditing);
  useEffect(() => {
    // If we were in edit mode and now we're not, save the name if it changed
    if (prevIsEditingRef.current && !isEditing) {
      if (editedNodeName.trim() && editedNodeName.trim() !== nodeName) {
        if (data.onNodeUpdate && data.nodeIndex !== undefined) {
          data.onNodeUpdate(data.nodeIndex, { name: editedNodeName.trim() });
        } else if (data.onSectionUpdate && data.sectionIndex !== undefined) {
          data.onSectionUpdate(data.sectionIndex, { name: editedNodeName.trim() });
        }
      }
    }
    prevIsEditingRef.current = isEditing;
  }, [isEditing, editedNodeName, nodeName, data]);

  const colors = getFieldTypeColor(node!.type);

  const handleSaveNodeName = useCallback(() => {
    if (editedNodeName.trim()) {
      // Try new structure first, fall back to legacy
      if (data.onNodeUpdate && data.nodeIndex !== undefined) {
        data.onNodeUpdate(data.nodeIndex, { name: editedNodeName.trim() });
      } else if (data.onSectionUpdate && data.sectionIndex !== undefined) {
        data.onSectionUpdate(data.sectionIndex, { name: editedNodeName.trim() });
      }
    }
    setIsEditingNodeName(false);
  }, [editedNodeName, data]);

  const handleNodeNameKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveNodeName();
    } else if (e.key === 'Escape') {
      setEditedNodeName(nodeName);
      setIsEditingNodeName(false);
    }
  }, [handleSaveNodeName, nodeName]);

  // Helper to get node name by identifier (supports both new and legacy)
  const getNodeName = useCallback((identifier: string | null | undefined): string => {
    if (data.nodes) {
      return getNodeNameUtil(identifier, data.nodes);
    }
    return getSectionNameUtil(identifier, data.sections);
  }, [data.nodes, data.sections]);

  // Determine border styling based on selection state
  const borderColor = isSelectedInContext ? "#3b82f6" : (selected ? "#3b82f6" : "#000000");
  const borderWidth = isSelectedInContext ? "3px" : undefined;
  const boxShadow = isSelectedInContext ? "0 0 0 3px rgba(59, 130, 246, 0.3)" : undefined;

  return (
    <CardBase
      shadowType="color"
      shadowColor={colors.shadowColor}
      backgroundColor="#ffffff"
      border={true}
      borderColor={borderColor}
      className={`min-w-[280px] max-w-[400px] ${className} ${isEditing ? 'noDrag' : ''}`}
      style={{ borderWidth, boxShadow }}
    >
      {/* Target Handle - all nodes have this on the left */}
      <Handle 
        type="target" 
        position={Position.Left}
        id="left"
        className="w-3 h-3 !bg-primary"
      />

      {/* Header with node type */}
      <div className={`flex items-center gap-2 px-3 py-2 ${colors.bg} border-b ${colors.border}`}>
        {headerContent ? (
          headerContent
        ) : (
          <>
            <IconProvider iconstring={getFieldTypeIcon(node!.type)} size={14} className={colors.text} />
            <span className={`flex-1 font-semibold text-xs uppercase ${colors.text}`}>
              {getFieldTypeLabel(node!.type)}
            </span>
            {node!.speaker && (
              <span className="text-xs font-medium text-gray-600 bg-white px-2 py-0.5 rounded">
                {node!.speaker}
              </span>
            )}
          </>
        )}
        {showEditButton && onEditToggle && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEditToggle();
            }}
            className="hover:bg-white/50 p-1 rounded transition-colors"
          >
            <IconProvider 
              iconstring={isEditing ? "fa-check" : "fa-edit"} 
              size={12} 
              className={colors.text}
            />
          </button>
        )}
      </div>

      {/* Node name label */}
      <div className="px-3 py-1 bg-gray-50 border-b text-xs text-muted-foreground flex items-center gap-1">
        <IconProvider iconstring="fa-layer-group" size={10} className="flex-shrink-0" />
        {isEditingNodeName || isEditing ? (
          <Input
            value={editedNodeName}
            onChange={(e) => setEditedNodeName(e.target.value)}
            onBlur={handleSaveNodeName}
            onKeyDown={handleNodeNameKeyDown}
            className="h-5 text-xs flex-1 px-1 py-0 noDrag"
            autoFocus={isEditingNodeName}
            onClick={(e) => e.stopPropagation()}
            placeholder="Enter node name"
          />
        ) : (
          <>
            <span className="flex-1">{nodeName}</span>
            {(data.onNodeUpdate || data.onSectionUpdate) && !isEditing && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditingNodeName(true);
                  setEditedNodeName(nodeName);
                }}
                className="hover:bg-gray-200 p-0.5 rounded transition-colors flex-shrink-0"
                title="Edit node name"
              >
                <IconProvider iconstring="fa-edit" size={10} className="text-gray-600" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Content - provided by specific node type */}
      <div className={`px-3 py-2 ${isEditing ? 'noDrag' : ''}`}>
        {children}
      </div>
    </CardBase>
  );
}

// Re-export utilities for convenience
export { getNodeName as getNodeNameUtil, getSectionName as getSectionNameUtil } from './dialogNodeUtils';
export { getFieldTypeIcon, getFieldTypeColor, getFieldTypeLabel } from './dialogNodeUtils';

export default BaseDialogNode;
